import React, { useEffect, useState } from 'react'
import { BrowserRouter as RouterModule, Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { DepotoCore } from 'depoto-core'
import { Carrier, CompanyCarrier, Order } from 'depoto-core'
import { Schema, updateOrder, updatePackages } from './'
import {
  Dashboard,
  Dispatch,
  DispatchList,
  GroupsList,
  ExternalLogin,
  Login,
  Main,
  NotFound404,
  OrderDetail,
  OrderGroupDetails,
  PublicHome,
  Settings,
} from '../views'
import { Loader, OrdersHistory } from '../components'
import { useCore, useLogin } from '../hooks'
import { GeoConfig } from 'depoto-core'
import { Fn } from 'depoto-core'
import { useQueryClient } from '@tanstack/react-query'
import SettingsView from '../views/Settings/SettingsView'
import { DisposalList } from '../views/Disposal/DisposalList'
import { DisposalCreate } from '../views/Disposal/DisposalCreate'
// import { initNotifications } from './Firebase'

let lastLocation = ''

type Props = {
  core: DepotoCore
}

export const Router: React.FC<Props> = ({ core }) => {
  const [isReady, setIsReady] = useState(false)
  const { setCore, currentOrder, setCurrentOrder, setCompany } = useCore()
  const { isLoggedIn, handleAuthInit, user } = useLogin()

  // Initialize core and auth state once on mount
  useEffect(() => {
    if (core) {
      setCore(core)
      handleAuthInit(core).finally(() => setIsReady(true))
    }
  }, [core])

  useEffect(() => {
    if (user?.company?.id) {
      setCompany(user.company as any)
    } else {
      setCompany(undefined)
    }
  }, [user])

  // Clear stale current order when navigating away
  useEffect(() => {
    if (!!currentOrder && !window.location.pathname.includes('order')) {
      setTimeout(() => setCurrentOrder(undefined))
    }
  }, [currentOrder?.updatedAt])

  const queryClient = useQueryClient()
  // Clear query cache on logout
  useEffect(() => {
    if (!isLoggedIn) {
      queryClient.clear()
    }
  }, [isLoggedIn])

  if (!isReady) {
    return <Loader />
  }
  return (
    <RouterModule>
      <Routes core={core} />
    </RouterModule>
  )
}

const Routes: React.FC<any> = ({ core }) => {
  const {
    currentOrder,
    setCurrentOrder,
    orderUpdates,
    setOrderUpdates,
    packagesUpdates,
    setPackageUpdates,
    setIsFetching,
  } = useCore()
  const { isLoggedIn } = useLogin()
  const location = useLocation()

  if (location?.pathname !== '/dashboard' && location?.search?.startsWith('?groupId')) {
    // hotfix get rid of groupId param outside of groups
    history.pushState(null, '', `${window.location.origin}${window.location.pathname}`)
  }

  const getOrder = async () => {
    setIsFetching(true)
    const res = await core?.services.order.getById(currentOrder?.id || 0, Schema.order.detail)
    if (res && res.id > 0) {
      const o = new Order(res)
      setIsFetching(false)
      setCurrentOrder(o)
    } else {
      setIsFetching(false)
    }
  }

  if (location.pathname !== lastLocation) {
    // console.log('loc changed', location)
    if (
      lastLocation.startsWith('/order') &&
      // !location.pathname.startsWith('/order') &&
      (Object.keys(orderUpdates || {}).length || Object.keys(packagesUpdates || {}).length)
    ) {
      // console.log('loc changed with extra', lastLocation, location, orderUpdates, packagesUpdates)
      if (confirm('Máte neuložené změny. Přejete si je uložit?')) {
        // console.log('update from router', { packagesUpdates, orderUpdates })
        if (orderUpdates && Object.keys(orderUpdates).length > 0) {
          updateOrder(orderUpdates, setOrderUpdates, currentOrder!, core, getOrder)
        }
        if (packagesUpdates && Object.keys(packagesUpdates).length > 0) {
          updatePackages(packagesUpdates, setPackageUpdates, core, getOrder)
        }
      } else {
        setTimeout(() => {
          setOrderUpdates(undefined)
          setPackageUpdates(undefined)
        }, 1000)
        // setTimeout(() => patchState({ orderUpdates: undefined, packagesUpdates: undefined }),500)
        //nope todo routing able to do things like this..
        // return (
        //   <Redirect
        //     to={{
        //       pathname: lastLocation,
        //       state: { from: location },
        //     }}
        //   />
        // )
      }
    }
    lastLocation = location.pathname
  }
  return (
    <Main isSettingsView={location.pathname.includes('/settings')}>
      <RootRedirect />
      {/*<TransitionGroup>*/}
      {/*  <CSSTransition timeout={300} classNames="reroute" key={location?.key}>*/}
      <Switch location={location}>
        <Route exact path="/">
          <PublicHome />
        </Route>
        <Route exact path="/login">
          <Login core={core} />
        </Route>
        <Route exact path="/external-login">
          <ExternalLogin core={core} />
        </Route>
        <AuthenticatedRoute exact path="/dashboard/history">
          <OrdersHistory />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/dashboard/:processStatus">
          <Dashboard />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/settings">
          <SettingsView />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/dashboard">
          <Dashboard />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/order/:orderId">
          <OrderDetail />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/order">
          <Dashboard />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/search/:searchString">
          <Dashboard />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/search">
          <Dashboard />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/groups">
          <GroupsList />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/group/:groupId">
          <OrderGroupDetails />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/dispatch/create">
          <Dispatch />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/disposal/create">
          <DisposalCreate />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/dispatch">
          <DispatchList />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/disposal">
          <DisposalList />
        </AuthenticatedRoute>
        <Route path="*">
          <NotFound404 />
        </Route>
      </Switch>
      {/*  </CSSTransition>*/}
      {/*</TransitionGroup>*/}
    </Main>
  )
}

const AuthenticatedRoute: React.FC<any> = ({ children, ...props }) => {
  const { isLoggedIn } = useLogin()
  return (
    <Route
      {...props}
      render={({ location }) =>
        isLoggedIn ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: location },
            }}
          />
        )
      }
    />
  )
}

const RootRedirect = () => {
  const { isLoggedIn } = useLogin()
  const location: any = useLocation()
  if (location.pathname === '/') {
    return (
      <Redirect
        to={{
          pathname: isLoggedIn ? '/dashboard' : '/login',
          state: { from: location },
        }}
      />
    )
  }
  return null
}

const reloadTokensAndLists = async (
  core: DepotoCore,
  isLoggedIn: boolean,
  setIsStrictDepotFilter: (isStrictDepotFilter: boolean) => void,
  childCompanyId?: number,
): Promise<void> => {
  const filters: any = {}
  if (childCompanyId && childCompanyId > 0) {
    filters.companies = [childCompanyId]
  }
  core.services.oauth.onLogoutEmitter
    .subscribe()
    .then(() => (window.location.pathname !== '/login' ? (window.location.pathname = '/login') : null))
  core.services.oauth.session.clientId = '37_5uneulcjp9oo0gs8cscc0occ0ccs48ssk8cwo0o0ws44s440wg' // TODO!!!!!
  core.services.oauth.session.clientSecret = '1xhuy9flepggcww0swkkc0wwws8s800c480wwwg4wkggwgg444' // TODO!!!! core fix!!!!!!!!
  if (window.depotoEnv === 'custom' && window.localStorage.getItem('depoto_server_custom_url')?.length) {
    core.services.oauth.session.clientCustomUri = `${window.localStorage.getItem('depoto_server_custom_url') || ''}`
    core.services.oauth.session.clientType = 'custom'
  }
  if (isLoggedIn) {
    const doesTokenExists = core.services.oauth?.session?.token?.accessToken?.length > 0
    const user = doesTokenExists ? await core.services.user.getMyProfile(Schema.user.detail) : null
    if (!user) {
      return core.services.oauth.logout()
    }
    const isStrictDepotFilter = window.localStorage.getItem('isStrictDepotFilter') === 'true'
    setIsStrictDepotFilter(isStrictDepotFilter)
  } else {
    core.services.oauth.logout()
  }
}

export const setSearchParams = (params: { [key: string]: string | number } | any) => {
  let queryStr = '?'
  Object.keys(params).forEach((k, i) => {
    // console.warn(k, params[k], i)
    if (i > 0) {
      queryStr += '&'
    }
    if (params[k] !== undefined && params[k] !== 'all') {
      queryStr += `${k}=${params[k]}`
    }
  })
  // console.warn(params, queryStr)
  const oldLocation = `${window.location.origin}${window.location.pathname}`
  history.pushState(null, '', `${oldLocation}${queryStr === '?' ? '' : queryStr}`)
}

export const useSearchParams = () => {
  const queryString = window.location.search ? window.location.search : ''
  const spArr = queryString.replace('?', '').split('&')
  const searchParams: any = {}
  for (let p of spArr) {
    const par = p.split('=')
    searchParams[par[0]] = par[1]
  }
  return searchParams
}
